import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '../App';
import { ASSETS } from '../constants/site';

function fmtErr(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return String(detail?.msg || detail);
}

export default function Login() {
  const { login, loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [twofa, setTwofa] = useState(false);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);

  useEffect(() => {
    api.get('/settings/twofa').then((r) => setTwofa(!!r.data.enabled)).catch(() => {});
  }, []);

  const sendCode = async () => {
    setOtpBusy(true);
    setError('');
    try {
      await api.post('/auth/otp/send', { phone });
      setOtpSent(true);
      toast.success('Verification code sent by SMS.');
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail));
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyCode = async () => {
    setOtpBusy(true);
    setError('');
    try {
      await api.post('/auth/otp/verify', { phone, code: otpCode });
      setPhoneVerified(true);
      toast.success('Phone verified.');
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail));
    } finally {
      setOtpBusy(false);
    }
  };

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(''); };

  const passwordMismatch = mode === 'register' && form.confirm.length > 0 && form.password !== form.confirm;
  const canSubmit = !busy
    && !(mode === 'register' && twofa && !phoneVerified)
    && !(mode === 'register' && (!form.confirm || form.password !== form.confirm));

  const submit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && form.password !== form.confirm) {
      setError("Passwords don't match — please re-enter them.");
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'signin') {
        await loginEmail(form.email, form.password);
        navigate('/account');
      } else {
        const res = await registerEmail(form.name, form.email, form.password, phone);
        if (res?.needs_verification) {
          setRegistered(form.email);
        } else {
          navigate('/account');
        }
      }
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resendActivation = async () => {
    try {
      await api.post('/auth/resend-verification', { email: registered, origin_url: window.location.origin });
      toast.success('Activation email sent again.');
    } catch {
      toast.error('Could not resend — try again in a moment.');
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail, origin_url: window.location.origin });
      setForgotSent(true);
    } catch {
      toast.error('Could not send the reset email. Try again.');
    } finally {
      setForgotBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl pl-11 pr-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600';

  return (
    <div className="min-h-screen bg-[#050B14] circuit-bg flex items-center justify-center px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="gold-ring rounded-3xl bg-[#0F1C33]/80 p-8 sm:p-10 max-w-md w-full"
        data-testid="login-page"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 transition-colors mb-6">
          <ArrowLeft size={13} /> Back to gslab.engineer
        </Link>
        <div className="text-center mb-8">
          <img src={ASSETS.icon} alt="GSLAB" className="h-14 w-14 rounded-xl mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl">
            {registered ? 'Check your inbox' : mode === 'signin' ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="text-sm text-slate-400 mt-2">Client portal, projects and plans in one place.</p>
        </div>

        {registered ? (
          <div data-testid="register-verify-notice" className="text-center">
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-6 mb-6">
              <Mail size={24} className="text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-slate-200 leading-relaxed">
                We sent an activation link to <span className="text-amber-300">{registered}</span>.
                Click it to activate your account — you'll be signed in automatically.
              </p>
              <p className="text-xs text-slate-500 mt-3">Link works for 24 hours. Check spam too.</p>
            </div>
            <button data-testid="resend-activation-button" onClick={resendActivation}
              className="text-xs border border-amber-500/40 text-amber-300 rounded-full px-6 py-2.5 hover:bg-amber-500/10 transition-colors">
              Resend activation email
            </button>
            <button data-testid="back-to-signin-button" onClick={() => { setRegistered(''); setMode('signin'); }}
              className="block mx-auto mt-4 text-xs text-slate-500 hover:text-amber-300 transition-colors">
              Back to sign in
            </button>
          </div>
        ) : (
        <>

        <button
          data-testid="login-google-button"
          onClick={login}
          className="w-full inline-flex items-center justify-center gap-3 border border-slate-600/70 hover:border-amber-500/50 text-slate-100 rounded-full px-6 py-3.5 transition-colors duration-300 mb-6"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <span className="flex-1 h-px bg-slate-800" />
          <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">OR WITH EMAIL</span>
          <span className="flex-1 h-px bg-slate-800" />
        </div>

        <div className="grid grid-cols-2 gap-1 bg-[#0B1528]/70 border border-slate-800 rounded-full p-1 mb-6" data-testid="login-mode-toggle">
          {[['signin', 'Sign in'], ['register', 'Create account']].map(([m, label]) => (
            <button key={m} data-testid={`login-mode-${m}`} onClick={() => { setMode(m); setError(''); }}
              className={`rounded-full py-2 text-sm font-semibold transition-colors duration-300 ${mode === m ? 'bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14]' : 'text-slate-400 hover:text-slate-200'}`}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'register' && (
            <div className="relative">
              <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="register-name-input" required placeholder="Your name" value={form.name} onChange={set('name')} className={inputCls} />
            </div>
          )}
          <div className="relative">
            <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="login-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set('email')} className={inputCls} />
          </div>
          <div className="relative">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="login-password-input" required type="password" minLength={8} placeholder="Password (8+ characters)" value={form.password} onChange={set('password')} className={inputCls} />
          </div>
          {mode === 'register' && (
            <div>
              <div className="relative">
                <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="register-confirm-password-input" required type="password" minLength={8}
                  placeholder="Confirm password" value={form.confirm} onChange={set('confirm')}
                  className={`${inputCls} ${passwordMismatch ? 'border-red-500/60' : form.confirm && form.password === form.confirm ? 'border-emerald-500/50' : ''}`} />
              </div>
              {passwordMismatch && (
                <p data-testid="password-mismatch-warning" className="text-xs text-red-400 mt-2 pl-1">Passwords don't match</p>
              )}
              {!passwordMismatch && form.confirm && form.password === form.confirm && (
                <p data-testid="password-match-msg" className="text-xs text-emerald-300 mt-2 pl-1">✓ Passwords match</p>
              )}
            </div>
          )}
          {mode === 'register' && twofa && (
            <div className="border border-slate-800 rounded-xl p-4 space-y-3" data-testid="twofa-block">
              <p className="font-mono-tech text-[10px] tracking-[0.25em] text-amber-300/90 uppercase">SMS Verification Required</p>
              <div className="flex gap-2">
                <input data-testid="register-phone-input" type="tel" required placeholder="Mobile number" value={phone}
                  onChange={(e) => { setPhone(e.target.value); setPhoneVerified(false); setOtpSent(false); }}
                  className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600" />
                <button type="button" data-testid="send-code-button" onClick={sendCode} disabled={otpBusy || !phone.trim() || phoneVerified}
                  className="text-xs border border-amber-500/40 text-amber-300 rounded-full px-4 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
                  {otpSent ? 'Resend' : 'Send code'}
                </button>
              </div>
              {otpSent && !phoneVerified && (
                <div className="flex gap-2">
                  <input data-testid="otp-code-input" inputMode="numeric" placeholder="6-digit code" value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600 font-mono-tech tracking-widest" />
                  <button type="button" data-testid="verify-code-button" onClick={verifyCode} disabled={otpBusy || otpCode.length < 4}
                    className="text-xs border border-emerald-500/40 text-emerald-300 rounded-full px-4 hover:bg-emerald-500/10 transition-colors disabled:opacity-40">
                    Verify
                  </button>
                </div>
              )}
              {phoneVerified && (
                <p data-testid="phone-verified-msg" className="text-xs text-emerald-300">✓ Phone verified</p>
              )}
            </div>
          )}
          {error && <p data-testid="login-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-2.5">{error}</p>}
          <button data-testid="login-submit-button" type="submit" disabled={!canSubmit}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] transition-shadow duration-300 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        {mode === 'signin' && (
          <button data-testid="forgot-password-link" onClick={() => setForgotOpen(!forgotOpen)}
            className="w-full text-center text-xs text-slate-500 hover:text-amber-300 transition-colors mt-5">
            Forgot your password?
          </button>
        )}

        {forgotOpen && mode === 'signin' && (
          <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} onSubmit={sendReset} className="mt-4 border-t border-slate-800 pt-4">
            {forgotSent ? (
              <p data-testid="forgot-sent-msg" className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-4 py-3">
                If that email has a password account, a reset link is on its way (check spam too).
              </p>
            ) : (
              <div className="flex gap-2">
                <input data-testid="forgot-email-input" required type="email" placeholder="Your account email" value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="flex-1 bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-xs outline-none transition-colors placeholder:text-slate-600" />
                <button data-testid="forgot-send-button" type="submit" disabled={forgotBusy}
                  className="text-xs border border-amber-500/40 text-amber-300 rounded-full px-4 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                  {forgotBusy ? '…' : 'Send link'}
                </button>
              </div>
            )}
          </motion.form>
        )}
        </>
        )}
      </motion.div>
    </div>
  );
}
